import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../../baseQuery';
import {
  ActFilter,
  AddMultipleOrdersResponse,
  BodyCreateOrder,
  BodyCreateOrderTemplate,
  BodyUpdateOrder,
  GetWorkersArgs,
  OrderFiltersResponse,
  OrdersQueryParams,
  QueryParamsType,
  RecponseGetOrdersType,
  ResponseCancelOrderByPlatformType,
  ResponseCreateOrderType,
  ResponseGetDocumentTypes,
  ResponseGetOrderTemplatesType,
  ResponseGetOrderType,
  ResponseGetTabType,
  ResponseGetTypesOfServicesType,
  ResponseGetUnitMeasuresType,
  ResponseGetWorkerType,
  SignMultipleOrdersRequest,
  SignMultipleOrdersResponse,
  Template,
  ValueOrderAgreement,
} from './type';

export interface transformResponseGetWorkersType {
  data: ResponseGetWorkerType[];
  meta?: {
    assigned_count: number;
    current_page: number;
    last_page: number;
    total: number;
  };
}

export interface GetWorkersResult {
  data: ResponseGetWorkerType[];
  meta?: transformResponseGetWorkersType['meta'];
}

export interface transformResponseGetOrderTemplatesType {
  data: Template[];
  meta: {
    current_page: number;
    last_page: number;
    total: number;
  };
}

type OrdersFilters = {
  create_date_range?: { start: string; end: string };
  date_start_range?: { start: string; end: string };
  date_end_range?: { start: string; end: string };
  date_paid_range?: { start: string; end: string };
  numbers_range?: { start: string; end: string };
  statuses?: string[];
  amount_from?: number;
  amount_to?: number;
  type_ids?: number[];
  payment_type_ids?: string[];
  customer_ids?: number[];
  worker_ids?: number[];
  preset_ids?: number[];
  act?: ActFilter;
  receipts?: { number?: string; statuses?: string[] };
  fns_receipts_seen_status?: 0 | 1;
};

function applyFiltersToParams(
  filters: OrdersFilters,
  target: URLSearchParams | Record<string, any>,
): void {
  const isURLSearchParams = target instanceof URLSearchParams;

  const setParam = (key: string, value: string | number) => {
    if (isURLSearchParams) {
      (target as URLSearchParams).set(key, String(value));
    } else {
      (target as Record<string, any>)[key] = value;
    }
  };

  if (filters.create_date_range) {
    setParam('create_date_range[start]', filters.create_date_range.start);
    setParam('create_date_range[end]', filters.create_date_range.end);
  }

  if (filters.date_start_range) {
    setParam('date_start_range[start]', filters.date_start_range.start);
    setParam('date_start_range[end]', filters.date_start_range.end);
  }

  if (filters.date_end_range) {
    setParam('date_end_range[start]', filters.date_end_range.start);
    setParam('date_end_range[end]', filters.date_end_range.end);
  }

  if (filters.date_paid_range) {
    setParam('date_paid_range[start]', filters.date_paid_range.start);
    setParam('date_paid_range[end]', filters.date_paid_range.end);
  }

  if (filters.numbers_range) {
    setParam('numbers_range[start]', filters.numbers_range.start ?? '');
    setParam('numbers_range[end]', filters.numbers_range.end ?? '');
  }

  if (filters.statuses?.length) {
    filters.statuses.forEach((status, index) => {
      setParam(`statuses[${index}]`, status);
    });
  }

  if (filters.amount_from !== undefined) {
    setParam('amount_from', filters.amount_from);
  }

  if (filters.amount_to !== undefined) {
    setParam('amount_to', filters.amount_to);
  }

  if (filters.type_ids?.length) {
    filters.type_ids.forEach((id, index) => {
      setParam(`type_ids[${index}]`, id);
    });
  }

  if (filters.payment_type_ids?.length) {
    filters.payment_type_ids.forEach((id, index) => {
      setParam(`payment_type_ids[${index}]`, id);
    });
  }

  if (filters.customer_ids?.length) {
    filters.customer_ids.forEach((id, index) => {
      setParam(`customer_ids[${index}]`, id);
    });
  }

  if (filters.worker_ids?.length) {
    filters.worker_ids.forEach((id, index) => {
      setParam(`worker_ids[${index}]`, id);
    });
  }

  if (filters.preset_ids?.length) {
    filters.preset_ids.forEach((id, index) => {
      setParam(`preset_ids[${index}]`, id);
    });
  }

  if (filters.act) {
    if (filters.act.number) {
      setParam('act[number]', filters.act.number);
    }
    if (filters.act.statuses?.length) {
      filters.act.statuses.forEach((status, index) => {
        setParam(`act[statuses][${index}]`, status);
      });
    }
    if (filters.act.create_date_range) {
      setParam('act[create_date_range][start]', filters.act.create_date_range.start);
      setParam('act[create_date_range][end]', filters.act.create_date_range.end);
    }
    if (filters.act.amount_from !== undefined) {
      setParam('act[amount_from]', filters.act.amount_from);
    }
    if (filters.act.amount_to !== undefined) {
      setParam('act[amount_to]', filters.act.amount_to);
    }
  }

  if (filters.receipts) {
    if (filters.receipts.number !== undefined) {
      setParam('receipts[number]', filters.receipts.number);
    }
    if (filters.receipts.statuses?.length) {
      filters.receipts.statuses.forEach((status, index) => {
        setParam(`receipts[statuses][${index}]`, status);
      });
    }
  }

  if (
    filters.fns_receipts_seen_status !== undefined &&
    filters.fns_receipts_seen_status !== null
  ) {
    setParam('fns_receipts_seen_status', filters.fns_receipts_seen_status);
  }
}

const getToken = () => localStorage.getItem('token');

const getAuthorizationHeader = () => {
  const token = getToken();
  return token ? `Bearer ${token}` : '';
};

const headersData = {
  'Content-Type': 'application/json',
  Accept: 'application/json',
  Authorization: getAuthorizationHeader(),
};

export const ordersApi = createApi({
  reducerPath: 'ordersApi',
  tagTypes: ['Orders', 'Order', 'Workers', 'TypesOfServices', 'OrdersTemplates'],
  refetchOnReconnect: true,
  keepUnusedDataFor: 3,
  baseQuery: baseQueryWithReauth(),
  endpoints: (build) => ({
    getOrders: build.query<RecponseGetOrdersType, OrdersQueryParams>({
      query: (params) => {
        const { page = 1, per_page = 50, tab, search, ...filters } = params;

        const searchParams = new URLSearchParams();

        searchParams.set('page', page.toString());
        searchParams.set('per_page', per_page.toString());

        const tabParams: Record<string, string> = {
          'customer-action-required': 'customer_action_required',
          'worker-action-required': 'worker_action_required',
          approving_volume: 'approving_volume',
          finished: 'finished',
          draft: 'draft',
        };

        if (tab && tab !== 'all' && tabParams[tab]) {
          searchParams.set(tabParams[tab], '1');
        }

        if (search) {
          searchParams.set('search', search);
        }

        // Применяем фильтры
        applyFiltersToParams(filters, searchParams);

        const url = `${
          process.env.REACT_APP_API_URL
        }/api/v2/orders?${searchParams.toString()}`;

        return {
          url,
          method: 'GET',
        };
      },
      providesTags: () => ['Orders'],
      serializeQueryArgs: ({ endpointName }) => {
        return endpointName;
      },
      merge: (currentCache, newItems, { arg }) => {
        if (arg.page === 1) {
          return newItems;
        }

        if (currentCache && newItems.data) {
          const existingIds = new Set((currentCache.data || []).map((order) => order.id));
          const appended = newItems.data.filter((order) => !existingIds.has(order.id));
          return {
            ...newItems,
            data: [...(currentCache.data || []), ...appended],
          };
        }
        return newItems;
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg !== previousArg;
      },
    }),
    getTabOrders: build.query<ResponseGetTabType, void>({
      query: () => ({
        url: `${process.env.REACT_APP_API_URL}/api/orders/tabs`,
        method: 'GET',
      }),
      providesTags: () => ['Orders'],
    }),
    getOrder: build.query<ResponseGetOrderType, { id: string | undefined }>({
      query: ({ id }) => ({
        url: `/orders/${id}`,
        method: 'GET',
      }),
      keepUnusedDataFor: 3,
      providesTags: ['Order'],
    }),
    getWorkers: build.query<GetWorkersResult, GetWorkersArgs | void>({
      query: ({
        forContract = false,
        unified_contract_type,
        search,
        page,
        per_page,
        genders,
        issues,
        categories,
        nationalities,
        contracts,
        limit_from,
        limit_to,
        date_add,
      } = {}) => {
        const params = new URLSearchParams();

        if (forContract) {
          params.set('filter', 'for_contract');
        } else {
          params.set('filter', 'for_select');
        }

        if (unified_contract_type) {
          params.set('unified_contract_type', unified_contract_type);
        }

        if (search?.trim()) {
          params.set('search', search.trim());
        }

        if (page != null) {
          params.set('page', String(page));
        }
        if (per_page != null) {
          params.set('per_page', String(per_page));
        }

        if (genders?.length) {
          genders.forEach((keyword) => params.append('genders[]', keyword));
        }
        if (issues?.length) {
          issues.forEach((keyword) => params.append('issues[]', keyword));
        }
        if (nationalities?.length) {
          nationalities.forEach((keyword) => params.append('nationalities[]', keyword));
        }
        if (categories?.length) {
          categories.forEach((keyword) => params.append('categories[]', keyword));
        }
        if (contracts?.length) {
          contracts.forEach((keyword) => params.append('contracts[]', keyword));
        }

        if (limit_from != null) {
          params.set('limit_from', String(limit_from));
        }
        if (limit_to != null) {
          params.set('limit_to', String(limit_to));
        }

        if (date_add?.start) {
          params.set('date_add[start]', date_add.start);
        }
        if (date_add?.end) {
          params.set('date_add[end]', date_add.end);
        }
        return {
          url: `/workers?${params.toString()}`,
          method: 'GET',
        };
      },
      providesTags: () => ['Workers'],
      transformResponse: (
        response: transformResponseGetWorkersType,
      ): GetWorkersResult => ({
        data: response?.data ?? [],
        meta: response?.meta,
      }),
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const { page, per_page, ...rest } = queryArgs ?? {};
        return `${endpointName}(${JSON.stringify(rest)})`;
      },
      merge: (currentCache, newItems, { arg }) => {
        if ((arg?.page ?? 1) === 1) {
          return newItems;
        }
        if (currentCache?.data?.length && newItems.data?.length) {
          return {
            ...newItems,
            data: [...currentCache.data, ...newItems.data],
          };
        }
        return newItems;
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg !== previousArg;
      },
    }),
    getTypesOfServices: build.query<ResponseGetTypesOfServicesType, void>({
      query: () => ({
        url: `/orders/types`,
        method: 'GET',
      }),
      providesTags: () => ['TypesOfServices'],
      transformResponse: (response: ResponseGetTypesOfServicesType) => ({
        items: response.items,
        default: response.default,
        meta: response.meta,
      }),
    }),
    getTypesFilters: build.query<ResponseGetTypesOfServicesType, QueryParamsType>({
      query: ({ page, per_page, search }) => ({
        url: `/orders/types`,
        method: 'GET',
        params: {
          page,
          per_page,
          ...(search ? { search } : {}),
        },
      }),
      providesTags: () => ['TypesOfServices'],
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const q = (queryArgs.search ?? '').trim();
        return `${endpointName}(${q})`;
      },
      merge: (currentCache, newItems, { arg }) => {
        if ((arg.page ?? 1) === 1) {
          return newItems;
        }
        if (currentCache?.items && newItems.items) {
          const seen = new Set<number>();
          const items = [...currentCache.items, ...newItems.items].filter((t) => {
            if (seen.has(t.id)) return false;
            seen.add(t.id);
            return true;
          });
          return {
            ...newItems,
            items,
            default: newItems.default ?? currentCache.default,
          };
        }
        return newItems;
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg !== previousArg;
      },
    }),
    createOrder: build.mutation<ResponseCreateOrderType, { body: BodyCreateOrder }>({
      query: ({ body }) => ({
        url: `/orders`,
        method: 'POST',
        body,
        headers: headersData,
      }),
      invalidatesTags: ['Orders'],
    }),
    setOrderAgreement: build.mutation<
      { message: string },
      { id: string; body: ValueOrderAgreement }
    >({
      query: ({ id, body }) => ({
        url: `/orders/${id}/set-volume`,
        method: 'POST',
        body,
        headers: headersData,
      }),
      invalidatesTags: (result, error, arg) => [{ type: 'Orders', id: arg.id }, 'Order'],
    }),
    selectWorker: build.mutation<
      { message: string },
      { id: string; body: { worker_id: string } }
    >({
      query: ({ id, body }) => ({
        url: `/orders/${id}/workers/${body.worker_id}/approve`,
        method: 'POST',
        headers: headersData,
      }),
      invalidatesTags: (result, error, arg) => [{ type: 'Orders', id: arg.id }, 'Order'],
    }),
    getResponceSelectWorker: build.query<
      GetWorkersResult,
      { id: string } & GetWorkersArgs
    >({
      query: ({
        id,
        search,
        page,
        per_page,
        genders,
        issues,
        categories,
        nationalities,
        contracts,
        limit_from,
        limit_to,
        date_add,
      }) => {
        const params = new URLSearchParams();
        if (search?.trim()) {
          params.set('search', search.trim());
        }
        if (genders?.length) {
          genders.forEach((keyword) => params.append('genders[]', keyword));
        }
        if (issues?.length) {
          issues.forEach((keyword) => params.append('issues[]', keyword));
        }
        if (nationalities?.length) {
          nationalities.forEach((keyword) => params.append('nationalities[]', keyword));
        }
        if (categories?.length) {
          categories.forEach((keyword) => params.append('categories[]', keyword));
        }
        if (contracts?.length) {
          contracts.forEach((keyword) => params.append('contracts[]', keyword));
        }
        if (limit_from != null) {
          params.set('limit_from', String(limit_from));
        }
        if (limit_to != null) {
          params.set('limit_to', String(limit_to));
        }
        if (date_add?.start) {
          params.set('date_add[start]', date_add.start);
        }
        if (date_add?.end) {
          params.set('date_add[end]', date_add.end);
        }
        if (page != null) {
          params.set('page', String(page));
        }
        if (per_page != null) {
          params.set('per_page', String(per_page));
        }
        const query = params.toString();

        return {
          url: `/orders/${id}/workers${query ? `?${query}` : ''}`,
          method: 'GET',
          headers: headersData,
        };
      },
      providesTags: () => ['Order'],
      transformResponse: (
        response: transformResponseGetWorkersType,
      ): GetWorkersResult => ({
        data: response?.data ?? [],
        meta: response?.meta,
      }),
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const { page, per_page, ...rest } = queryArgs ?? {};
        return `${endpointName}(${JSON.stringify(rest)})`;
      },
      merge: (currentCache, newItems, { arg }) => {
        if ((arg?.page ?? 1) === 1) {
          return newItems;
        }
        if (currentCache?.data?.length && newItems.data?.length) {
          return {
            ...newItems,
            data: [...currentCache.data, ...newItems.data],
          };
        }
        return newItems;
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg !== previousArg;
      },
    }),
    updateOrder: build.mutation<
      ResponseCreateOrderType,
      { id: string; body: BodyUpdateOrder }
    >({
      query: ({ id, body }) => ({
        url: `/orders/${id}`,
        method: 'PATCH',
        body,
        headers: headersData,
      }),
      invalidatesTags: (result, error, arg) => [{ type: 'Orders', id: arg.id }, 'Order'],
    }),
    acceptManySummary: build.mutation<
      { count: number; total_amount: string },
      OrdersFilters
    >({
      query: (filters) => {
        const params: Record<string, any> = {};
        applyFiltersToParams(filters, params);

        return {
          url: `/orders/summary`,
          method: 'GET',
          params,
          headers: headersData,
        };
      },
      invalidatesTags: ['Orders'],
    }),
    sendActForOrder: build.mutation({
      query: ({ id }) => ({
        url: `/orders/${id}/accept`,
        method: 'POST',
        headers: headersData,
      }),
      invalidatesTags: ['Order'],
    }),
    actionForOrders: build.mutation<unknown, { id: string; action: string }>({
      query: ({ id, action }) => ({
        url: `/orders/${id}/${action}`,
        method: 'POST',
        headers: headersData,
      }),
      invalidatesTags: (result, error, arg) => [{ type: 'Orders', id: arg.id }, 'Order'],
    }),
    addMultipleOrders: build.mutation<AddMultipleOrdersResponse, FormData>({
      query: (formData) => ({
        url: `/orders/upload`,
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: ['Orders'],
    }),
    addMultipleOrdersSetVolume: build.mutation<AddMultipleOrdersResponse, FormData>({
      query: (formData) => ({
        url: `/orders/set-volume`,
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: ['Orders'],
    }),
    signMultipleOrders: build.mutation<
      SignMultipleOrdersResponse,
      SignMultipleOrdersRequest
    >({
      query: (data) => {
        const { items, ...filters } = data;
        const params: Record<string, any> = {};

        applyFiltersToParams(filters, params);

        return {
          url: `/orders/accept-many`,
          method: 'POST',
          body: { items },
          params,
          headers: {
            Accept: 'application/json',
            Authorization: getAuthorizationHeader(),
          },
        };
      },
      invalidatesTags: ['Orders'],
    }),
    getOrderTemplates: build.query<ResponseGetOrderTemplatesType, { page?: number }>({
      query: (params) => {
        const { page = 1 } = params;
        const searchParams = new URLSearchParams();
        searchParams.set('page', page.toString());
        searchParams.set('per_page', '20');
        return { url: `/orders/presets?${searchParams.toString()}`, method: 'GET' };
      },
      transformResponse: (response: ResponseGetOrderTemplatesType) => response,
      providesTags: ['Orders'],
      serializeQueryArgs: ({ endpointName }) => endpointName,
      merge: (currentCache, newItems, { arg }) => {
        if ((arg.page ?? 1) === 1) {
          return newItems;
        }
        if (currentCache?.data && newItems.data) {
          const seen = new Set<string>();
          const data = [...currentCache.data, ...newItems.data].filter((t) => {
            const key = String(t.id);
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          });
          return {
            ...newItems,
            data,
          };
        }
        return newItems;
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg !== previousArg;
      },
    }),
    getOrderTemplatesFilters: build.query<
      transformResponseGetOrderTemplatesType,
      QueryParamsType
    >({
      query: ({ page, per_page, search }) => ({
        url: '/orders/presets',
        method: 'GET',
        params: {
          page,
          per_page,
          ...(search ? { search } : {}),
        },
        headers: {
          Accept: 'application/json',
          Authorization: getAuthorizationHeader(),
        },
      }),
      transformResponse: (
        response: transformResponseGetOrderTemplatesType,
      ): transformResponseGetOrderTemplatesType => {
        const data = response?.data ?? [];
        return {
          data,
          meta: response?.meta ?? {
            current_page: 1,
            last_page: 1,
            total: data.length,
          },
        };
      },
      providesTags: ['OrdersTemplates'],
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const q = (queryArgs.search ?? '').trim();
        return `${endpointName}(${q})`;
      },
      merge: (currentCache, newItems, { arg }) => {
        if ((arg.page ?? 1) === 1) {
          return newItems;
        }
        if (currentCache?.data && newItems.data) {
          const seen = new Set<string>();
          const data = [...currentCache.data, ...newItems.data].filter((t) => {
            const key = String(t.id);
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          });
          return {
            ...newItems,
            data,
          };
        }
        return newItems;
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg !== previousArg;
      },
    }),
    createOrderTemplate: build.mutation<Template, BodyCreateOrderTemplate>({
      query: (body) => ({
        url: '/orders/presets',
        method: 'POST',
        body,
        headers: headersData,
      }),
      invalidatesTags: ['Orders'],
    }),
    getUnitMeasures: build.query<ResponseGetUnitMeasuresType, void>({
      query: () => ({
        url: `${process.env.REACT_APP_API_URL}/api/orders/unit-measures`,
        method: 'GET',
        headers: {
          Accept: 'application/json',
          Authorization: getAuthorizationHeader(),
        },
      }),
      providesTags: ['Orders'],
    }),
    deleteOrderTemplate: build.mutation<{ message: string }, { id: string }>({
      query: ({ id }) => ({
        url: `/orders/presets/${id}`,
        method: 'DELETE',
        headers: {
          Accept: 'application/json',
          Authorization: getAuthorizationHeader(),
        },
      }),
      invalidatesTags: ['Orders'],
    }),
    getBeneficiaryDocumentsData: build.mutation<any, { beneficiary_id: string }>({
      query: ({ beneficiary_id }) => ({
        url: `/get-beneficiary-documents-data`,
        method: 'POST',
        params: {
          beneficiary_id: beneficiary_id,
        },
        headers: {
          Accept: 'application/json',
          Authorization: getAuthorizationHeader(),
        },
      }),
      invalidatesTags: ['Orders'],
    }),
    getResendBeneficiaryDocumentsData: build.mutation<
      { message: string; code: string },
      { beneficiary_id: string }
    >({
      query: ({ beneficiary_id }) => ({
        url: `/resend-beneficiary-documents-data`,
        method: 'POST',
        params: {
          beneficiary_id: beneficiary_id,
        },
        headers: {
          Accept: 'application/json',
          Authorization: getAuthorizationHeader(),
        },
      }),
      invalidatesTags: ['Orders'],
    }),
    cancelOrderByPlatform: build.mutation<
      ResponseCancelOrderByPlatformType,
      { orderId: string }
    >({
      query: ({ orderId }) => ({
        url: `/cancel-order-by-platform`,
        method: 'POST',
        body: { order_id: orderId },
        headers: headersData,
      }),
      invalidatesTags: ['Orders', 'Order'],
    }),
    replayPaymentsByPlatform: build.mutation<void, { orderId: string }>({
      query: ({ orderId }) => ({
        url: `/withdraw-order`,
        method: 'POST',
        body: { order_id: orderId },
        headers: headersData,
      }),
      invalidatesTags: ['Orders', 'Order'],
    }),
    getOrderFilters: build.query<OrderFiltersResponse, void>({
      query: () => ({
        url: `${process.env.REACT_APP_API_URL}/api/orders/filters`,
        method: 'GET',
      }),
      providesTags: () => ['Orders'],
    }),
    getDocumentTypes: build.query<ResponseGetDocumentTypes[], void>({
      query: () => ({
        url: `/orders/docs/types`,
        method: 'GET',
      }),
      providesTags: () => ['Orders'],
    }),
    generateDocsSet: build.mutation<
      { file_id?: string | number },
      { items?: string[]; types: string[]; filters?: OrdersQueryParams }
    >({
      query: (body) => ({
        url: `/orders/docs/generate-docs-set`,
        method: 'POST',
        body,
        headers: headersData,
      }),
    }),
    getOrderUploadTemplate: build.mutation<{ blob: Blob; headers: Headers }, void>({
      query: () => ({
        url: `/orders/upload-template`,
        method: 'POST',
        headers: {
          Accept:
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/octet-stream',
          Authorization: getAuthorizationHeader(),
        },
        responseHandler: async (response: Response) => ({
          blob: await response.blob(),
          headers: response.headers,
        }),
      }),
    }),
    getTemplateSetVolume: build.mutation<{ blob: Blob; headers: Headers }, void>({
      query: () => ({
        url: `/orders/volume-upload-template`,
        method: 'POST',
        headers: {
          Accept:
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/octet-stream',
          Authorization: getAuthorizationHeader(),
        },
        responseHandler: async (response: Response) => ({
          blob: await response.blob(),
          headers: response.headers,
        }),
      }),
    }),
  }),
});

export const {
  useGetOrderQuery,
  useGetTabOrdersQuery,
  useGetOrdersQuery,
  useGetWorkersQuery,
  useCreateOrderMutation,
  useGetBeneficiaryDocumentsDataMutation,
  useGetUnitMeasuresQuery,
  useGetResendBeneficiaryDocumentsDataMutation,
  useGetTypesOfServicesQuery,
  useLazyGetTypesOfServicesQuery,
  useGetTypesFiltersQuery,
  useSendActForOrderMutation,
  useUpdateOrderMutation,
  useAcceptManySummaryMutation,
  useActionForOrdersMutation,
  useAddMultipleOrdersMutation,
  useAddMultipleOrdersSetVolumeMutation,
  useSignMultipleOrdersMutation,
  useGetOrderTemplatesQuery,
  useGetOrderTemplatesFiltersQuery,
  useCreateOrderTemplateMutation,
  useDeleteOrderTemplateMutation,
  useCancelOrderByPlatformMutation,
  useReplayPaymentsByPlatformMutation,
  useSetOrderAgreementMutation,
  useSelectWorkerMutation,
  useGetResponceSelectWorkerQuery,
  useGetOrderFiltersQuery,
  useGetDocumentTypesQuery,
  useGenerateDocsSetMutation,
  useGetOrderUploadTemplateMutation,
  useGetTemplateSetVolumeMutation,
} = ordersApi;
